import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
// utils
import axiosInstance from 'src/utils/axios';
import { RANSOMWARE_HOST_API } from 'src/config-global';
// types
import {
  IVictim,
  ISector,
  IGroupDetail,
  IVictimsFilters,
  IGroupSummary,
  IVictimsResponse,
  IRansomwareStats,
  IVictimsSummary,
  IIocsGroupEntry,
  IIocsByGroupResponse,
  IAnalyticsKpis,
  IAnalyticsTimeline,
  IAnalyticsBreakdown,
  IAnalyticsHeatmap,
  IAnalyticsGrowth,
  IAnalyticsCalendar,
  IAnalyticsInsights,
  IAnalyticsGraph,
  IRepeatTargets,
  IGroupProfile,
  ISectorProfile,
  ICountryProfile,
  IAnalyticsDimension,
  IAnalyticsGranularity,
  IAnalyticsFilterParams,
  ICampaignsResponse,
  ILeakSite,
  IOrganizationMatch,
  IIntelCvesResponse,
  IIntelToolsResponse,
  IIntelTtpsResponse,
  IIntelIocsSummary,
  IYaraListEntry,
  IYaraRules,
  INotesList,
  INoteContent,
  INegotiations,
  IAnalyticsMeta,
  ISearchResponse,
  ICveDetail,
  IIocLookup,
} from 'src/types/ransomware';

// ----------------------------------------------------------------------

const isCanceled = (error: any) =>
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

// ----------------------------------------------------------------------
// Generic GET hook: one request, aborted + refetched whenever `key` changes.
// The gateway caches upstream calls, so refetches are cheap.

function useGet<T>(path: string | null, key: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async (bypassCache = false) => {
    if (!path) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const res = await axiosInstance.get<T>(`${RANSOMWARE_HOST_API}${path}`, {
        signal: controller.signal,
        // manual refresh: tell the browser to revalidate instead of serving its
        // fresh cached copy. The server ETag still yields a cheap 304 if the
        // data is unchanged, and the gateway lru-cache still absorbs it — so a
        // refresh never blindly re-hits the upstream source.
        ...(bypassCache && { headers: { 'Cache-Control': 'no-cache' } }),
      });
      setData(res.data);
    } catch (err) {
      if (!isCanceled(err)) setError(err);
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
    // key is the intentional dependency — path is derived from it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    // key changed → a different resource. Clear the previous resource's data so
    // consumers (e.g. the victim drawer) don't flash stale data while the new
    // one loads. This runs only on key change, not on refresh (same key).
    setData(null);
    setError(null);
    // normal load — honors the browser cache within max-age
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  // Refresh button: force a revalidation (ignores any event arg from onClick)
  const refresh = useCallback(() => fetchData(true), [fetchData]);

  return { data, loading, error, refresh };
}

// ----------------------------------------------------------------------

export function useGetRansomwareStats() {
  const { data, loading, error, refresh } = useGet<IRansomwareStats>('/stats', 'stats');
  return { stats: data, statsLoading: loading, statsError: error, refresh };
}

export function useGetVictimsSummary(country?: string) {
  const query = country ? `?country=${encodeURIComponent(country)}` : '';
  const { data, loading, error, refresh } = useGet<IVictimsSummary>(
    `/victims/summary${query}`,
    `summary:${country || ''}`
  );
  return { summary: data, summaryLoading: loading, summaryError: error, refresh };
}

export function useGetRecentVictims() {
  const { data, loading, error, refresh } = useGet<IVictimsResponse>(
    '/victims/recent',
    'recent'
  );
  return {
    victims: data?.victims ?? [],
    count: data?.count ?? 0,
    victimsLoading: loading,
    victimsError: error,
    refresh,
  };
}

// Server-paginated victim listing (keyset/cursor). Upstream requires at least
// one filter, so with none selected we fall back to /victims/recent (the full
// history) instead of erroring; a free-text query routes to /victims/search.
//
// Cursor pagination is forward-sequential: to reach page N you must have
// fetched page N-1, whose response carried the cursor for page N. We cache one
// cursor per visited page in `cursorsRef` (index = page; [null] = first page)
// so the DataGrid's prev/next navigation resolves to the right cursor. `total`
// comes from the backend so the grid shows a truthful "N of M". Changing the
// filters/search OR the page size invalidates the cached cursors and resets to
// page 0.
export function useGetVictimsPaged(filters: IVictimsFilters, search: string) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const cursorsRef = useRef<(string | null)[]>([null]);
  const [rows, setRows] = useState<IVictim[]>([]);
  const [rowCount, setRowCount] = useState(0);

  // the query WITHOUT paging params — its identity, when it changes, resets paging
  const basePath = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.group) params.set('group', filters.group);
    if (filters.country) params.set('country', filters.country);
    if (filters.sector) params.set('sector', filters.sector);
    if (filters.year) params.set('year', filters.year);

    const trimmed = search.trim();
    const searching = trimmed.length >= 2;
    const hasFilter = params.toString().length > 0;

    if (searching) {
      // /victims/search now accepts `year` server-side, so no client-side cut
      params.set('q', trimmed);
      return `/victims/search?${params.toString()}`;
    }
    if (hasFilter) return `/victims/?${params.toString()}`;
    return '/victims/recent';
  }, [filters.group, filters.country, filters.sector, filters.year, search]);

  // reset paging whenever the underlying query changes
  useEffect(() => {
    cursorsRef.current = [null];
    setRowCount(0);
    setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
  }, [basePath]);

  // build the effective path for the current page (cursor + limit)
  const cursor = cursorsRef.current[paginationModel.page] ?? null;
  const sep = basePath.includes('?') ? '&' : '?';
  const parts = [`limit=${paginationModel.pageSize}`];
  if (cursor) parts.push(`cursor=${encodeURIComponent(cursor)}`);
  const path = `${basePath}${sep}${parts.join('&')}`;

  const { data, loading, error, refresh } = useGet<IVictimsResponse>(
    path,
    `victims-paged:${path}`
  );

  useEffect(() => {
    if (!data) return;
    setRows(data.victims ?? []);

    const pageStart = paginationModel.page * paginationModel.pageSize;
    // prefer the exact total; fall back to an estimate that keeps "next" alive
    const total =
      typeof data.total === 'number'
        ? data.total
        : pageStart + (data.victims?.length ?? 0) + (data.hasMore ? paginationModel.pageSize : 0);
    setRowCount(total);

    // cache the cursor that fetches the NEXT page
    if (data.hasMore && data.nextCursor) {
      cursorsRef.current[paginationModel.page + 1] = data.nextCursor;
    }
    // paginationModel is read-only snapshot here; data is the trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onPaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      setPaginationModel((prev) => {
        // page size changed → cursors were minted for the old size; start over
        if (model.pageSize !== prev.pageSize) {
          cursorsRef.current = [null];
          return { page: 0, pageSize: model.pageSize };
        }
        // forward jumps are only valid to a page we already hold a cursor for
        // (prev/next always satisfy this); ignore anything else defensively
        if (model.page > prev.page && cursorsRef.current[model.page] === undefined) {
          return prev;
        }
        return model;
      });
    },
    []
  );

  return {
    victims: rows,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    victimsLoading: loading,
    victimsError: error,
    refresh,
  };
}

export function useGetVictim(id: string | null) {
  const { data, loading, error } = useGet<IVictim>(
    id ? `/victims/${encodeURIComponent(id)}` : null,
    `victim:${id || ''}`
  );
  return { victim: data, victimLoading: loading, victimError: error };
}

export function useGetSectors() {
  const { data, loading, error, refresh } = useGet<{ count: number; sectors: ISector[] }>(
    '/sectors',
    'sectors'
  );
  return {
    sectors: data?.sectors ?? [],
    sectorsLoading: loading,
    sectorsError: error,
    refresh,
  };
}

export function useGetGroups() {
  const { data, loading, error, refresh } = useGet<{ count: number; groups: IGroupSummary[] }>(
    '/groups',
    'groups'
  );
  return {
    groups: data?.groups ?? [],
    count: data?.count ?? 0,
    groupsLoading: loading,
    groupsError: error,
    refresh,
  };
}

export function useGetGroupDetail(name: string | null) {
  const { data, loading, error, refresh } = useGet<IGroupDetail>(
    name ? `/groups/${encodeURIComponent(name)}` : null,
    `group:${name || ''}`
  );
  return { group: data, groupLoading: loading, groupError: error, refresh };
}

export function useGetIocs(type?: string) {
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  const { data, loading, error, refresh } = useGet<{ count: number; groups: IIocsGroupEntry[] }>(
    `/iocs${query}`,
    `iocs:${type || ''}`
  );
  return {
    groups: data?.groups ?? [],
    count: data?.count ?? 0,
    iocsLoading: loading,
    iocsError: error,
    refresh,
  };
}

export function useGetIocsByGroup(group: string | null, type?: string) {
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  const { data, loading, error, refresh } = useGet<IIocsByGroupResponse>(
    group ? `/iocs/${encodeURIComponent(group)}${query}` : null,
    `iocs-group:${group || ''}:${type || ''}`
  );
  return { iocs: data, iocsLoading: loading, iocsError: error, refresh };
}

// ----------------------------------------------------------------------
// Analytics layer — computed correlation endpoints. Everything below shares
// the same filter params so one filter bar can drive the whole dashboard.
// Query strings are built deterministically (sorted keys) so the hook key is
// stable for a given filter combination.

function buildQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  Object.keys(params)
    .sort()
    .forEach((k) => {
      const v = params[k];
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function useGetAnalyticsKpis(filters: IAnalyticsFilterParams) {
  const path = `/analytics/kpis${buildQuery(filters)}`;
  const { data, loading, error, refresh } = useGet<IAnalyticsKpis>(path, path);
  return { kpis: data, kpisLoading: loading, kpisError: error, refresh };
}

export function useGetAnalyticsTimeline(
  filters: IAnalyticsFilterParams,
  options?: { granularity?: IAnalyticsGranularity; groupBy?: IAnalyticsDimension; top?: number }
) {
  const path = `/analytics/timeline${buildQuery({
    ...filters,
    granularity: options?.granularity,
    groupBy: options?.groupBy,
    top: options?.top,
  })}`;
  const { data, loading, error, refresh } = useGet<IAnalyticsTimeline>(path, path);
  return { timeline: data, timelineLoading: loading, timelineError: error, refresh };
}

export function useGetAnalyticsBreakdown(
  dimension: IAnalyticsDimension,
  filters: IAnalyticsFilterParams,
  limit?: number
) {
  const path = `/analytics/breakdown${buildQuery({ ...filters, dimension, limit })}`;
  const { data, loading, error, refresh } = useGet<IAnalyticsBreakdown>(path, path);
  return { breakdown: data, breakdownLoading: loading, breakdownError: error, refresh };
}

export function useGetAnalyticsHeatmap(
  rows: IAnalyticsDimension,
  cols: IAnalyticsDimension,
  filters: IAnalyticsFilterParams,
  options?: { topRows?: number; topCols?: number }
) {
  const path = `/analytics/heatmap${buildQuery({
    ...filters,
    rows,
    cols,
    topRows: options?.topRows,
    topCols: options?.topCols,
  })}`;
  const { data, loading, error, refresh } = useGet<IAnalyticsHeatmap>(path, path);
  return { heatmap: data, heatmapLoading: loading, heatmapError: error, refresh };
}

export function useGetAnalyticsGrowth(
  dimension: IAnalyticsDimension,
  filters: IAnalyticsFilterParams,
  options?: { windowDays?: number; limit?: number; min?: number }
) {
  const path = `/analytics/growth${buildQuery({
    ...filters,
    dimension,
    windowDays: options?.windowDays,
    limit: options?.limit,
    min: options?.min,
  })}`;
  const { data, loading, error, refresh } = useGet<IAnalyticsGrowth>(path, path);
  return { growth: data, growthLoading: loading, growthError: error, refresh };
}

export function useGetAnalyticsCalendar(filters: IAnalyticsFilterParams, year?: number) {
  const path = `/analytics/calendar${buildQuery({ ...filters, year })}`;
  const { data, loading, error, refresh } = useGet<IAnalyticsCalendar>(path, path);
  return { calendar: data, calendarLoading: loading, calendarError: error, refresh };
}

export function useGetAnalyticsInsights(limit?: number) {
  const path = `/analytics/insights${buildQuery({ limit })}`;
  const { data, loading, error, refresh } = useGet<IAnalyticsInsights>(path, path);
  return {
    insights: data?.insights ?? [],
    generatedAt: data?.generatedAt ?? null,
    insightsLoading: loading,
    insightsError: error,
    refresh,
  };
}

export function useGetAnalyticsGraph(
  focus: string | null,
  options?: { victimLimit?: number; top?: number }
) {
  const path = `/analytics/graph${buildQuery({
    focus: focus ?? undefined,
    victimLimit: options?.victimLimit,
    top: options?.top,
  })}`;
  const { data, loading, error, refresh } = useGet<IAnalyticsGraph>(path, path);
  return { graph: data, graphLoading: loading, graphError: error, refresh };
}

export function useGetRepeatTargets(filters: IAnalyticsFilterParams, limit?: number) {
  const path = `/analytics/repeat-targets${buildQuery({ ...filters, limit })}`;
  const { data, loading, error, refresh } = useGet<IRepeatTargets>(path, path);
  return {
    repeatTargets: data?.items ?? [],
    repeatTargetsLoading: loading,
    repeatTargetsError: error,
    refresh,
  };
}

export function useGetGroupProfile(name: string | null) {
  const path = name ? `/analytics/profile/group/${encodeURIComponent(name)}` : null;
  const { data, loading, error, refresh } = useGet<IGroupProfile>(path, path ?? 'group-profile:none');
  return { profile: data, profileLoading: loading, profileError: error, refresh };
}

export function useGetSectorProfile(name: string | null) {
  const path = name ? `/analytics/profile/sector/${encodeURIComponent(name)}` : null;
  const { data, loading, error, refresh } = useGet<ISectorProfile>(path, path ?? 'sector-profile:none');
  return { profile: data, profileLoading: loading, profileError: error, refresh };
}

export function useGetCountryProfile(code: string | null) {
  const path = code ? `/analytics/profile/country/${encodeURIComponent(code)}` : null;
  const { data, loading, error, refresh } = useGet<ICountryProfile>(path, path ?? 'country-profile:none');
  return { profile: data, profileLoading: loading, profileError: error, refresh };
}

// ----------------------------------------------------------------------
// Investigation: campaigns, leak sites, organization history

export function useGetCampaigns(
  filters: IAnalyticsFilterParams,
  options?: { minSize?: number; gapDays?: number; limit?: number; activeOnly?: boolean }
) {
  const path = `/analytics/campaigns${buildQuery({
    ...filters,
    minSize: options?.minSize,
    gapDays: options?.gapDays,
    limit: options?.limit,
    activeOnly: options?.activeOnly ? 'true' : undefined,
  })}`;
  const { data, loading, error, refresh } = useGet<ICampaignsResponse>(path, path);
  return {
    campaigns: data?.campaigns ?? [],
    campaignParams: data?.params ?? null,
    campaignsLoading: loading,
    campaignsError: error,
    refresh,
  };
}

export function useGetLeakSites(filters: IAnalyticsFilterParams, limit?: number) {
  const path = `/analytics/leak-sites${buildQuery({ ...filters, limit })}`;
  const { data, loading, error, refresh } = useGet<{ count: number; items: ILeakSite[] }>(
    path,
    path
  );
  return {
    leakSites: data?.items ?? [],
    leakSitesLoading: loading,
    leakSitesError: error,
    refresh,
  };
}

export function useGetOrganization(q: string | null) {
  const path = q && q.trim().length >= 2 ? `/analytics/organization${buildQuery({ q })}` : null;
  const { data, loading, error } = useGet<{ query: string; matches: IOrganizationMatch[] }>(
    path,
    path ?? 'organization:none'
  );
  return {
    matches: data?.matches ?? [],
    organizationLoading: loading,
    organizationError: error,
  };
}

// ----------------------------------------------------------------------
// Technical intel aggregates

export function useGetIntelCves(limit?: number) {
  const path = `/analytics/intel/cves${buildQuery({ limit })}`;
  const { data, loading, error, refresh } = useGet<IIntelCvesResponse>(path, path);
  return { cves: data, cvesLoading: loading, cvesError: error, refresh };
}

export function useGetIntelTools(limit?: number) {
  const path = `/analytics/intel/tools${buildQuery({ limit })}`;
  const { data, loading, error, refresh } = useGet<IIntelToolsResponse>(path, path);
  return { tools: data, toolsLoading: loading, toolsError: error, refresh };
}

export function useGetIntelTtps() {
  const path = '/analytics/intel/ttps';
  const { data, loading, error, refresh } = useGet<IIntelTtpsResponse>(path, path);
  return { ttps: data, ttpsLoading: loading, ttpsError: error, refresh };
}

export function useGetIntelIocsSummary() {
  const path = '/analytics/intel/iocs-summary';
  const { data, loading, error, refresh } = useGet<IIntelIocsSummary>(path, path);
  return { iocsSummary: data, iocsSummaryLoading: loading, iocsSummaryError: error, refresh };
}

// ----------------------------------------------------------------------
// Arsenal: YARA rules, ransom notes, negotiations (per-actor)

export function useGetYaraList() {
  const { data, loading, error, refresh } = useGet<{ count: number; groups: IYaraListEntry[] }>(
    '/arsenal/yara',
    'yara-list'
  );
  return {
    yaraGroups: data?.groups ?? [],
    yaraCount: data?.count ?? 0,
    yaraLoading: loading,
    yaraError: error,
    refresh,
  };
}

export function useGetYaraByGroup(group: string | null) {
  const path = group ? `/arsenal/yara/${encodeURIComponent(group)}` : null;
  const { data, loading, error } = useGet<IYaraRules>(path, path ?? 'yara:none');
  return { yara: data, yaraLoading: loading, yaraError: error };
}

export function useGetNotesByGroup(group: string | null) {
  const path = group ? `/arsenal/notes/${encodeURIComponent(group)}` : null;
  const { data, loading, error } = useGet<INotesList>(path, path ?? 'notes:none');
  return { notes: data, notesLoading: loading, notesError: error };
}

export function useGetNoteContent(group: string | null, note: string | null) {
  const path =
    group && note
      ? `/arsenal/notes/${encodeURIComponent(group)}/${encodeURIComponent(note)}`
      : null;
  const { data, loading, error } = useGet<INoteContent>(path, path ?? 'note:none');
  return { note: data, noteLoading: loading, noteError: error };
}

export function useGetNegotiations(group: string | null) {
  const path = group ? `/arsenal/negotiations/${encodeURIComponent(group)}` : null;
  const { data, loading, error } = useGet<INegotiations>(path, path ?? 'negotiations:none');
  return { negotiations: data, negotiationsLoading: loading, negotiationsError: error };
}

// ----------------------------------------------------------------------
// Meta / handling, global search, CVE entity, IOC reverse lookup

export function useGetAnalyticsMeta() {
  const { data, loading, error, refresh } = useGet<IAnalyticsMeta>('/analytics/meta', 'meta');
  return { meta: data, metaLoading: loading, metaError: error, refresh };
}

export function useSearch(q: string | null) {
  const trimmed = (q ?? '').trim();
  const path = trimmed.length >= 1 ? `/analytics/search${buildQuery({ q: trimmed })}` : null;
  const { data, loading, error } = useGet<ISearchResponse>(path, path ?? 'search:none');
  return { results: data?.groups ?? [], searchLoading: loading, searchError: error };
}

export function useGetCveDetail(id: string | null) {
  const path = id ? `/analytics/intel/cve/${encodeURIComponent(id)}` : null;
  const { data, loading, error, refresh } = useGet<ICveDetail>(path, path ?? 'cve:none');
  return { cve: data, cveLoading: loading, cveError: error, refresh };
}

export function useIocLookup(value: string | null) {
  const trimmed = (value ?? '').trim();
  const path = trimmed.length >= 3 ? `/analytics/intel/ioc${buildQuery({ value: trimmed })}` : null;
  const { data, loading, error } = useGet<IIocLookup>(path, path ?? 'ioc-lookup:none');
  return { lookup: data, lookupLoading: loading, lookupError: error };
}
